import { useEffect, useState } from "react";
import { DocumentMetadata } from "../../../models";
import { PUBLICATIE_STATUS } from "../../../models/document-metadata";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { addForm, addFormSet, changeFormIndex, FormState, removeForm } from "../../../store/reducers/document-metadata";
import { selectDossier, selectMetadata, selectStepper } from "../../../store/selectors";
import { matchStatus } from "../../../utils/Metadata";
import { useOrganisaties } from "../../../utils/OrganisatiesHelper";
import { StringSanitizer } from "../../../utils/StringSanitizer";
import { ContainerAtom, HeadingH2Atom, IconAtom, SpacerAtom, TableAtom } from "../../atoms";
import { RowState } from "../../atoms/table";
import {
  IconButtonMolecule,
  PopupDepublicerenDocumentMolecule,
  PopupDepublicerenDossierMolecule,
  PopupMolecule,
  SpinnerPopupMolecule,
} from "../../molecules";
import { ContentRow } from "../../molecules/content-row";

const stringSanitizer = new StringSanitizer();

interface UploadRow {
  icon: JSX.Element;
  button: JSX.Element | null;
}

function DeleteButton(
  props: Readonly<{
    meta: DocumentMetadata;
    mode: "create" | "update";
    onDepublish: () => any;
    index: number;
    disabled: boolean;
  }>
) {
  if (matchStatus(props.meta, PUBLICATIE_STATUS.ingetrokken)) return <></>;

  return (
    <IconButtonMolecule
      icon={"icon-remove-blue"}
      size={"large"}
      key={`remove_upload_${props.index}`}
      id={`remove_upload_${props.index}`}
      title={
        "Trek dit document in. Een ingetrokken document is niet meer zichtbaar op open.overheid.nl, de metadata zullen nog wel beschikbaar zijn."
      }
      onClick={() => {
        props.onDepublish();
      }}
      type={"default"}
      removeButton
      disabled={props.disabled}
    />
  );
}

export function SidebarOrganism() {
  const stateDocument = useAppSelector(selectMetadata);
  const stateDossier = useAppSelector(selectDossier);
  const { data: dataDocument, fetching, localValidationError, documentError, setIndex } = stateDocument;
  const { data: dossierData } = stateDossier;
  const stateStepper = useAppSelector(selectStepper);
  const { flow, currentStep } = stateStepper;

  const publishDisabled =
    dataDocument.length > stateDocument.formIndex &&
    dataDocument?.[stateDocument.formIndex]?.[stateDocument.formIndex]?.meta?.plooiIntern?.publicatiestatus ===
      "_intrekking";

  const dispatch = useAppDispatch();
  const mode = useAppSelector((s) => s.meta.mode);

  const [popupDepubliceren, setPopupDepubliceren] = useState(false);
  const [spinnerDepubliceren, setSpinnerDepubliceren] = useState(false);
  const [popupErrors, setPopupErrors] = useState(false);
  const [fetchingText, setFetchingText] = useState<string | undefined>(undefined);
  const [pidDepubliceren, setPidDepubliceren] = useState<string>();
  const [tableIndexRemove, setTableIndexRemove] = useState<number>();
  const [indexRemove, setIndexRemove] = useState<number>();
  const { publicerendePartijen } = useOrganisaties();

  const documentErrorIndexList = documentError.indexList;
  const pid = dataDocument[0]?.[0]?.meta?.document?.pid;

  const dpcUpload = typeof pid === "string" && stringSanitizer.extractIdentifierFromPid(pid).startsWith("dpc-");

  const actievePublisher = (index: number) =>
    dataDocument?.[index][0]?.meta?.document?.publisher
      ? publicerendePartijen.some(
          ({ id, label }) =>
            id === dataDocument[index][0].meta.document.publisher.id &&
            label === dataDocument[index][0].meta.document.publisher.label
        )
      : false;

  const bijlageToevoegenDisabled = (index: number) =>
    dataDocument?.[index][0].meta?.plooiIntern?.publicatiestatus === "_intrekking";

  const isDossierView = flow === "Dossier" && currentStep === 1;

  useEffect(() => {
    setPopupErrors(
      (documentErrorIndexList?.length ?? 0) > 0 && documentErrorIndexList![0].index !== 0 && !localValidationError
    );
  }, [documentErrorIndexList]);

  useEffect(() => {
    setFetchingText(undefined);
    if (fetching) {
      if (dataDocument.length <= 0) {
        setFetchingText("Eén moment geduld, documenten worden opgehaald");
      } else if (spinnerDepubliceren) {
        setFetchingText("Eén moment geduld, document wordt ingetrokken");
      } else if (mode === "create") {
        setFetchingText("Eén moment geduld, document wordt geüpload");
      } else if (mode === "update") {
        setFetchingText("Eén moment geduld, document wordt bewerkt");
      }
    } else if (spinnerDepubliceren) {
      setSpinnerDepubliceren(false);
    }
  }, [fetching, spinnerDepubliceren]);

  const removeAttachment = (index: number) => {
    dispatch(removeForm(index));
  };

  const addAttachment = (index: number) => {
    dispatch(changeFormIndex({ index: 0, setIndex: index }));
    dispatch(addForm());
    dispatch(changeFormIndex({ index: stateDocument.data[index].length, setIndex: index }));
  };

  const onChangeIndex = (index: number, setIndex: number) => {
    dispatch(changeFormIndex({ index, setIndex }));
  };

  const hasDeleteButton = (index: number) => {
    const isFirstDocumentInCreateMode = stateDocument.mode === "create" && index === 0;
    return ((!dpcUpload && !isFirstDocumentInCreateMode) || flow === "Dossier") && !isDossierView;
  };

  const headingText = () =>
    `${flow === "Dossier" ? "Dossier" : "Documenten"} ${mode === "create" ? "uploaden" : "bewerken"}`;
  const tableHeadingText = () =>
    flow === "Dossier" ? dossierData.titel || "Titel dossier" : "Gerelateerde documenten";

  const renderTitleWithIcon = (index: number, result: any, tableIndex: number) => {
    return (
      <ContentRow
        index={index}
        result={result}
        tableIndex={tableIndex}
        onClick={onChangeIndex}
        dossierView={flow === "Dossier" && currentStep === 1}
        isSelected={stateDocument.formIndex === index && tableIndex === stateDocument.setIndex}
      ></ContentRow>
    );
  };

  const renderDeleteButton = (index: number, result: any, tableIndex: number) => {
    return hasDeleteButton(index) ? (
      <DeleteButton
        disabled={
          publishDisabled || dpcUpload || dataDocument[0].at(index)?.meta.plooiIntern?.publicatiestatus === "pending"
        }
        meta={result.meta}
        mode={mode}
        onDepublish={() => {
          setPidDepubliceren(result.meta.document.pid);
          setTableIndexRemove(tableIndex);
          setIndexRemove(index);
          setPopupDepubliceren(!popupDepubliceren);
        }}
        index={index}
      />
    ) : null;
  };

  const rows = (form: FormState[], tableIndex: number) => {
    return form.map((result, index) => {
      return {
        icon: renderTitleWithIcon(index, result, tableIndex),
        button: renderDeleteButton(index, result, tableIndex),
      };
    });
  };

  const addAttachmentButton = (index: number) => {
    if (rows?.length === 0 || isDossierView) return undefined;

    return (
      <IconButtonMolecule
        text={"Bijlage"}
        id={"voeg_bijlage_toe_" + index}
        title={"Voeg hier een bijlage toe."}
        onClick={() => addAttachment(index)}
        type={"default"}
        icon={"icon-plus-shape"}
        disabled={bijlageToevoegenDisabled(index) || !actievePublisher(index)}
      />
    );
  };

  const renderTable = () => {
    const data: FormState[][] = dataDocument;
    const selectedTable = stateDocument?.setIndex;
    if (!data || data.length === 0) {
      return (
        <TableAtom
          key="empty-table"
          theads={[tableHeadingText()]}
          rows={[]}
          columnWidth={["100%"]}
          highlight={undefined}
          states={[]}
          allowSelected={false}
        />
      );
    }

    return data.map((form: FormState[], index: number) => {
      const tableKey = form[0]?.meta.document.identifiers.join("-") || `table-${index}`;

      return (
        <TableAtom
          key={tableKey}
          theads={index === 0 ? [tableHeadingText()] : undefined}
          rows={rows(form, index).map((row: UploadRow) => [row.icon, row.button])}
          columnWidth={["100%"]}
          highlight={stateDocument.formIndex}
          addButton={addAttachmentButton(index)}
          states={getTableRowStates(form, index)}
          allowSelected={selectedTable === index && !isDossierView}
        />
      );
    });
  };

  const getTableRowStates = (form: FormState[], setIndex: number) => {
    return rows(form, setIndex).map((_, index: number) => {
      const errors = documentErrorIndexList?.find(
        (errorIndex) => errorIndex.setIndex === setIndex && errorIndex.index === index
      );
      if (errors !== undefined) return { index: index, state: RowState.error };
      if (stateDocument.data?.[stateDocument.setIndex]?.[index]?.meta.document.pid !== undefined)
        return { index: index, state: RowState.success };
      return { index: index, state: RowState.default };
    });
  };

  const renderAddDocumentButton = () => {
    if (flow === "Dossier" && currentStep === 2) {
      return (
        <div>
          <IconButtonMolecule
            text={"Document"}
            id={"documenten_toevoegen"}
            title={"Nieuwe Documenten toevoegen"}
            onClick={() => dispatch(addFormSet())}
            type={"primary"}
            icon={"icon-plus-white"}
          />
        </div>
      );
    }
    return null;
  };

  const dossierIntrekkenButton = () => {
    if (flow === "Dossier" && currentStep === 1 && mode === "update") {
      return (
        <div>
          <IconButtonMolecule
            text={"Dossier intrekken"}
            id={"dossier_intrekken"}
            title={"Dossier intrekken"}
            onClick={() => {
              setPidDepubliceren(dossierData.pid);
              setPopupDepubliceren(!popupDepubliceren);
            }}
            type={"blue"}
            icon={"icon-remove-blue"}
            size="large"
          />
        </div>
      );
    }
    return null;
  };

  const renderPopupDepubliceren = () => {
    if (!popupDepubliceren) return null;

    if (pidDepubliceren) {
      if (flow === "Dossier" && currentStep === 1) {
        return (
          <PopupDepublicerenDossierMolecule
            depublicerenId={pidDepubliceren}
            close={() => setPopupDepubliceren(!popupDepubliceren)}
          />
        );
      }
      return (
        <PopupDepublicerenDocumentMolecule
          depublicerenId={pidDepubliceren}
          close={() => setPopupDepubliceren(!popupDepubliceren)}
        />
      );
    } else
      return (
        <PopupMolecule
          title={"Weet u zeker dat u dit document wilt verwijderen?"}
          cancelButton={{
            text: "Doorgaan met uploaden",
            onClick: () => {
              setPopupDepubliceren(!popupDepubliceren);
            },
          }}
          okeButton={{
            text: "Document verwijderen",
            onClick: () => {
              if (tableIndexRemove !== undefined && indexRemove !== undefined) {
                onChangeIndex(0, tableIndexRemove);
                removeAttachment(indexRemove);
              }
              setPopupDepubliceren(!popupDepubliceren);
            },
          }}
        />
      );
  };

  const renderPopupErrors = () => {
    if (popupErrors) {
      return (
        <PopupMolecule
          title="Aanleverfouten"
          text={
            <div>
              <p>
                Uw documenten zijn aangeleverd. In deze set zijn{" "}
                {dataDocument[setIndex].length - (documentErrorIndexList?.length ?? 0)} document(en) goed verwerkt, deze
                zijn groen getoond in de tabel.
              </p>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                <IconAtom icon={"icon-alert-red"} size={"large"} alt={""} />
                <p>{documentErrorIndexList?.length ?? 0} document(-en) bevatte(n) een fout.</p>
              </div>
              <p>U wordt verzocht deze fout(-en) te herstellen en de document(en) opnieuw aan te leveren.</p>
            </div>
          }
          cancelButton={{
            text: "Oke",
            onClick: () => setPopupErrors(!popupErrors),
          }}
        />
      );
    }
    return null;
  };

  const renderSpinner = () => (fetchingText ? <SpinnerPopupMolecule text={fetchingText} /> : null);

  return (
    <ContainerAtom type="flex">
      <HeadingH2Atom id={"sidebar-heading"}>{headingText()}</HeadingH2Atom>
      <SpacerAtom space={"26px"} />
      {renderTable()}
      <SpacerAtom space={4} />
      {renderAddDocumentButton()}
      {dossierIntrekkenButton()}
      {renderPopupDepubliceren()}
      {renderPopupErrors()}
      {renderSpinner()}
    </ContainerAtom>
  );
}
